import { useState, useEffect } from "react";
import { modulesAPI, usersAPI } from "../../services/api";
import {
  Box, Typography, Button, Card, CardContent, Grid,
  Select, MenuItem, FormControl, InputLabel, Alert, Autocomplete, TextField as MuiTextField,
  CircularProgress, Chip, Avatar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

export default function EnrolmentManager() {
  const [users, setUsers] = useState([]);
  const [paths, setPaths] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [failedEnrolments, setFailedEnrolments] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, pathsRes] = await Promise.all([
        usersAPI.getUsers(),
        modulesAPI.getPaths()
      ]);
      const agents = usersRes.data.users.filter(u => u.role.role_name === "agent" && u.is_active);
      setUsers(agents);
      setPaths(pathsRes.data);
    } catch { setError("Failed to load data."); }
    finally { setLoading(false); }
  };

  const handleEnrol = async () => {
    if (!selectedUser || !selectedPath) {
      setError("Please select both an agent and a learning path.");
      return;
    }
    try {
      await modulesAPI.enrolAgent({ user_id: selectedUser, path_id: selectedPath });
      setFailedEnrolments([]);
      const user = users.find(u => u.user_id === selectedUser);
      const path = paths.find(p => p.path_id === selectedPath);
      setSuccess(`${user?.full_name} enrolled in "${path?.path_name}" successfully.`);
      setSelectedUser("");
      setSelectedPath("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to enrol agent.");
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { Email: "agent@example.com", "Learning Path": "New Agent Onboarding 2026" },
      { Email: "agent2@example.com", "Learning Path": "RAF Claims Management" }
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enrolments");
    XLSX.writeFile(wb, "bulk_enrolment_template.xlsx");
  };

  const exportBulkResults = () => {
    const rows = bulkResults.map(r => ({
      "Email": r.email,
      "Learning Path": r.pathName,
      "Status": r.status === "success" ? "Success" : "Failed",
      "Message": r.message
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enrolment Results");
    XLSX.writeFile(wb, "enrolment_results.xlsx");
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkLoading(true);
    setBulkResults([]);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      const results = [];
      for (const row of rows) {
        const email = row["Email"] || row["email"];
        const pathName = row["Learning Path"] || row["learning_path"] || row["Path"];
        if (!email || !pathName) {
          results.push({ email, pathName, status: "error", message: "Missing email or path name" });
          continue;
        }
        const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        const path = paths.find(p => p.path_name.toLowerCase() === pathName.toLowerCase());
        if (!user) {
          results.push({ email, pathName, status: "error", message: "Agent not found" });
          continue;
        }
        if (!path) {
          results.push({ email, pathName, status: "error", message: "Learning path not found" });
          continue;
        }
        try {
          await modulesAPI.enrolAgent({ user_id: user.user_id, path_id: path.path_id });
          results.push({ email, pathName, status: "success", message: "Enrolled successfully" });
        } catch (err) {
          results.push({ email, pathName, status: "error", message: err.response?.data?.detail || "Failed" });
        }
      }
      setBulkResults(results);
      const successCount = results.filter(r => r.status === "success").length;
      setSuccess(`Bulk enrolment complete: ${successCount}/${results.length} successful`);
    } catch (err) {
      setError("Failed to process Excel file.");
    } finally {
      setBulkLoading(false);
      e.target.value = "";
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#1a237e">Enrolments</Typography>
        <Typography variant="body2" color="text.secondary">
          Assign agents to learning paths
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={3} color="#1a237e">
            Enrol an Agent
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Autocomplete
                fullWidth
                options={users}
                getOptionLabel={u => `${u.full_name} (${u.email}) — ${u.team?.team_name || ""}`}
                value={users.find(u => u.user_id === selectedUser) || null}
                onChange={(e, val) => setSelectedUser(val ? val.user_id : "")}
                isOptionEqualToValue={(opt, val) => opt.user_id === val.user_id}
                renderOption={(props, u) => (
                  <li {...props} key={u.user_id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "#1a237e" }}>
                        {u.first_name[0]}{u.last_name[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{u.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email} — {u.team?.team_name}</Typography>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={params => <MuiTextField {...params} label="Search Agent" required />}
              />
              <Autocomplete
                fullWidth
                options={paths}
                getOptionLabel={p => p.path_name}
                value={paths.find(p => p.path_id === selectedPath) || null}
                onChange={(e, val) => setSelectedPath(val ? val.path_id : "")}
                isOptionEqualToValue={(opt, val) => opt.path_id === val.path_id}
                renderOption={(props, p) => (
                  <li {...props} key={p.path_id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{p.path_name}</Typography>
                      {p.description && <Typography variant="caption" color="text.secondary">{p.description}</Typography>}
                    </Box>
                  </li>
                )}
                renderInput={params => <MuiTextField {...params} label="Search Learning Path" required />}
              />
              <Button fullWidth variant="contained" size="large"
                startIcon={<PersonAddIcon />} onClick={handleEnrol}
                disabled={!selectedUser || !selectedPath}
                sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", py: 1.5 }}>
                Enrol Agent
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="#1a237e">Bulk Enrol from Excel</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {bulkResults.length > 0 && (
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportBulkResults}>
                Export Results
              </Button>
            )}
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate} size="small">
              Download Template
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload an Excel file with columns: <b>Email</b> and <b>Learning Path</b>
        </Typography>
        <Button variant="contained" component="label" startIcon={<UploadFileIcon />}
          disabled={bulkLoading}
          style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
          {bulkLoading ? "Processing..." : "Upload Excel File"}
          <input type="file" hidden accept=".xlsx,.xls" onChange={handleBulkUpload} />
        </Button>
        {bulkResults.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Results: {bulkResults.filter(r => r.status === "success").length} enrolled, {bulkResults.filter(r => r.status !== "success").length} failed
            </Typography>
            <TableContainer sx={{ maxHeight: 450, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell><b>Email</b></TableCell>
                    <TableCell><b>Learning Path</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell><b>Message</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkResults.map((r, i) => (
                    <TableRow key={i} sx={{ bgcolor: r.status === "success" ? "#f1f8e9" : "#fff8f8" }}>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.pathName}</TableCell>
                      <TableCell>
                        <Chip label={r.status === "success" ? "Success" : "Failed"} size="small"
                          color={r.status === "success" ? "success" : "error"} />
                      </TableCell>
                      <TableCell>{r.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      {failedEnrolments.length > 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: 2, border: "1px solid #ffcdd2" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2} color="error.main">
              ⚠️ Failed Enrolments ({failedEnrolments.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#ffebee" }}>
                    <TableCell><b>Agent</b></TableCell>
                    <TableCell><b>Email</b></TableCell>
                    <TableCell><b>Reason</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedEnrolments.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell>{f.name || "—"}</TableCell>
                      <TableCell>{f.email || "—"}</TableCell>
                      <TableCell><Chip label={f.reason} size="small" color="error" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" sx={{ mt: 1 }} onClick={() => setFailedEnrolments([])}>Clear</Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
